export const BLOGS = [
  /* ─────────────────────────────────────────────────────────────
     POST 1: Cybersecurity
  ───────────────────────────────────────────────────────────── */
  {
    id: "cybersecurity-the-digital-shield",
    slug: "cybersecurity-the-digital-shield",
    title: "Cybersecurity: The Digital Shield of Our Time",
    excerpt:
      "Data is the most powerful tool in the world. Here's how you can become one of its guardians — and why Nigeria needs you to.",
    category: "Career Guide",
    readTime: "6 min read",
    date: "2025-10-03",
    coverGradient: "linear-gradient(135deg, #00114c 0%, #003399 100%)",
    coverImage: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=900&q=80&auto=format&fit=crop",
    quote: {
      text: "We are lucky enough to be born in a time when the Internet is here.",
      author: "Gary Vaynerchuk",
    },
    content: [
      {
        type: "paragraph",
        text: "The internet age brought with it many advancements, including the proliferation of data and information, and the crucial need to protect them. Information, I believe, is the most powerful tool in the world; a simple idea can change everything. Data, then, is the compilation of this information. Given its immense power, data requires robust protection, which is where cybersecurity experts, our digital superheroes, come in.",
      },
      {
        type: "paragraph",
        text: "In today's world, cybersecurity has become one of the most vital careers. As data equals power, its protection is paramount. With an increasingly globalized world, the demand for cybersecurity grows daily. It's safe to say that in the future, cybersecurity will become even more valuable. Take Nigeria, for instance: in July 2024, the federal government announced plans to digitalize 80% of government activities by 2025, a move to modernize the public sector. Here we are in 2025, and these initiatives are well underway.",
      },
      {
        type: "heading",
        text: "What Do Cybersecurity Professionals Do?",
      },
      {
        type: "paragraph",
        text: "Cybersecurity experts are responsible for safeguarding an organization's data and digital assets from unauthorized access, theft, damage, and loss. They also identify potential security threats, leaks, and breaches, and implement measures to prevent attacks. In simpler terms, it's like finding holes in your house where rats could enter and then sealing them to prevent damage.",
      },
      {
        type: "paragraph",
        text: "A cybersecurity expert can work across various fields and industries, including finance, healthcare, government, retail, and e-commerce. As long as there are digital assets to protect, a cybersecurity expert is indispensable.",
      },
      {
        type: "heading",
        text: "Required Skills & Education",
      },
      {
        type: "paragraph",
        text: "Now that you know what cybersecurity experts do, what's next? Let's explore how to get into this field, including the required skills, education, and criteria. Essential technical skills include:",
      },
      {
        type: "bullets",
        items: [
          "Scripting (e.g., Python)",
          "Controls and frameworks",
          "Intrusion detection",
          "Network security control",
          "Incident response",
          "Cloud security (especially as more businesses migrate to the cloud)",
        ],
      },
      {
        type: "paragraph",
        text: "Can a complete beginner learn these skills? Absolutely. You can start with training programs from Google, Coursera, Udemy, or even us at Innkeeper Academy. While a degree in cybersecurity or a related field is not strictly necessary, it is an advantage. If you're already in tech, perhaps you've studied data analysis or data science, it's even easier, as you'll already be familiar with certain codes and tools.",
      },
      {
        type: "paragraph",
        text: "Beyond technical expertise, certain soft skills are crucial for thriving as a cybersecurity expert, such as:",
      },
      {
        type: "bullets",
        items: [
          "Communication",
          "Collaboration",
          "Risk management",
          "Adaptability",
          "Critical thinking",
        ],
      },
      {
        type: "heading",
        text: "Certifications",
      },
      {
        type: "paragraph",
        text: "While formal education is valuable, a certification acts as a recognized stamp of expertise. Many cybersecurity certifications are available, each with unique requirements and benefits. Some of the most popular include:",
      },
      {
        type: "cert-list",
        items: [
          {
            badge: "CISSP",
            badgeColor: "#00114c",
            label: "Certified Information Systems Security Professional",
            desc: "Globally recognized, certifies expertise in designing, implementing, and managing cybersecurity programs. Requires five years of experience across eight security domains and is sought for roles like security consultant and IT director.",
          },
          {
            badge: "Security+",
            badgeColor: "#29aae2",
            label: "CompTIA Security+",
            desc: "An entry-level certification, validates foundational cybersecurity knowledge. Covers network, operational, threat, application, data, host, access control, and cryptography topics. Ideal for IT professionals, it's a prerequisite for advanced certifications and recognized for roles like junior IT auditor.",
          },
          {
            badge: "CEH",
            badgeColor: "#f58220",
            label: "Certified Ethical Hacker",
            desc: "Focuses on ethical hacking tools and techniques to identify vulnerabilities and implement countermeasures. Covers reconnaissance, scanning, system hacking, malware, social engineering, and securing platforms.",
          },
          {
            badge: "CISM",
            badgeColor: "#00114c",
            label: "Certified Information Security Manager",
            desc: "For information security managers, focusing on governance, risk management, program development, and incident management. Valued for leadership roles such as CISO.",
          },
        ],
      },
      {
        type: "heading",
        text: "Career Opportunities",
      },
      {
        type: "paragraph",
        text: "After gaining cybersecurity skills, can you secure an entry-level job? Definitely. Some common entry-level roles for cybersecurity professionals include information security analyst, information security specialist, digital forensic examiner, IT auditor, penetration tester, network administrator, and help desk technician.",
      },
      {
        type: "paragraph",
        text: "Advanced roles can encompass executive positions like Chief Information Security Officer (CISO), strategic roles such as Security Architect, specialized technical roles like Malware Analyst, Penetration Tester, and Cryptographer, and leadership roles like Cybersecurity Director and Incident Responder Manager.",
      },
      {
        type: "cta",
        title: "Kickstart Your Cybersecurity Journey",
        body: "If you're looking for a great place to start, Innkeeper Academy is an excellent choice. Check out our cybersecurity curriculum and pricing today.",
        primaryLabel: "Explore Curriculum",
        primaryHref:
          "https://drive.google.com/file/d/1c1HXdgN62xQIeVsFVSpyX0zZmWA8doxC/view?usp=drive_link",
        secondaryLabel: "Apply Now",
        secondaryHref: "https://innkeeper.work/apply-now",
        note: "Questions? academy@innkeeper.work • +234 813 701 1735",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────
     POST 2: How to Start a Career in Tech in Lagos
  ───────────────────────────────────────────────────────────── */
  {
    id: "start-tech-career-lagos",
    slug: "start-tech-career-lagos",
    title: "How to Start a Career in Tech in Lagos",
    excerpt:
      "Knowing that tech has opportunities and knowing how to break in are two very different things. Here's the honest, step-by-step path — written for you.",
    category: "Step-by-Step",
    readTime: "8 min read",
    date: "2026-04-01",
    coverGradient: "linear-gradient(135deg, #001a6e 0%, #00114c 50%, #0a0a2e 100%)",
    coverImage: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=900&q=80&auto=format&fit=crop",
    quote: null,
    content: [
      {
        type: "paragraph",
        text: "Lagos is one of Africa's most vibrant tech ecosystems. From Yaba — nicknamed 'Yabacon Valley' — to the growing startup scenes in Victoria Island and Lekki, opportunities in tech are real, they are growing, and they are accessible to anyone willing to put in the work.",
      },
      {
        type: "paragraph",
        text: "But here's what nobody tells you: knowing that tech has opportunities and knowing how to actually break into it are two very different things. This guide is written specifically for you.",
      },
      {
        type: "paragraph",
        text: "If you've been asking yourself — 'Where do I even start? What skill should I learn? How long will it take? Can I really do this without a computer science degree?' — this guide is written specifically for you.",
      },
      {
        type: "paragraph",
        text: "We'll walk you through a clear, honest, step-by-step path to starting a tech career in Lagos. And by the end, you'll know exactly what to do next.",
      },
      {
        type: "heading",
        text: "Step 1: Understand Why Tech is Worth Pursuing in Lagos",
        step: 1,
      },
      {
        type: "paragraph",
        text: "Before you commit to learning anything, you need conviction. Here's why tech is genuinely worth your time:",
      },
      {
        type: "benefit-list",
        items: [
          {
            label: "High demand, limited supply.",
            detail:
              "Nigerian companies — and global companies hiring remotely — constantly struggle to find skilled tech talent. That gap is your opportunity.",
          },
          {
            label: "Remote work is real.",
            detail:
              "A frontend developer in Lagos can work for a company in the UK, Canada, or the US without leaving home.",
          },
          {
            label: "No degree required.",
            detail:
              "The tech industry judges you by what you can build, not by what school you attended.",
          },
          {
            label: "Income potential is significant.",
            detail:
              "Entry-level tech roles start from ₦150,000–₦400,000/month. Mid-level and senior roles command far more, especially for remote positions.",
          },
          {
            label: "It compounds.",
            detail:
              "Skills you learn today keep increasing in value. Every project and certification adds to your profile permanently.",
          },
        ],
      },
      {
        type: "reality",
        text: "Tech is not a get-rich-quick scheme. It requires consistent effort over months. But the return on that investment — in income, flexibility, and career growth — is hard to match in almost any other field.",
      },
      {
        type: "heading",
        text: "Step 2: Choose the Right Tech Path for You",
        step: 2,
      },
      {
        type: "paragraph",
        text: "One of the biggest mistakes beginners make is trying to learn everything. Tech is a wide field. Picking one direction and going deep is far more effective than spreading yourself thin.",
      },
      {
        type: "paragraph",
        text: "Here are the most in-demand paths in Lagos right now:",
      },
      {
        type: "card-grid",
        items: [
          {
            icon: "🖥️",
            title: "Frontend Web Development",
            desc: "You build the parts of websites and apps that users see and interact with. Languages: HTML, CSS, JavaScript, React. Great for visual thinkers and detail-oriented people.",
          },
          {
            icon: "⚙️",
            title: "Backend Web Development",
            desc: "You build the logic, databases, and servers that power applications behind the scenes. Languages: Node.js, Python, PHP. Great for logical, systems-oriented thinkers.",
          },
          {
            icon: "🔐",
            title: "Cybersecurity",
            desc: "You protect organisations from digital threats, data breaches, and attacks. One of the fastest-growing and highest-paying paths globally — and severely underrepresented in Nigeria.",
          },
          {
            icon: "🌐",
            title: "Networking (CCNA)",
            desc: "You design, manage, and troubleshoot computer networks. The Cisco CCNA certification is globally recognised and opens doors in telecoms, banking, and enterprise IT.",
          },
          {
            icon: "🤖",
            title: "AI & Machine Learning",
            desc: "You build systems that learn from data — powering everything from recommendation engines to fraud detection. A high-ceiling path for those who enjoy maths and problem-solving.",
          },
          {
            icon: "📋",
            title: "Project Management",
            desc: "You coordinate teams, timelines, and deliverables to ensure tech projects are delivered successfully. Ideal if you're a strong communicator with organisational skills — no coding required.",
          },
        ],
      },
      {
        type: "tip",
        text: "Ask yourself three questions: What kind of problems do I enjoy solving? Do I prefer working with visuals, logic, people, or data? What kind of role do I see myself in 2 years? Your honest answers will point you toward the right track.",
      },
      {
        type: "heading",
        text: "Step 3: Get Structured Training (This is Where Most People Go Wrong)",
        step: 3,
      },
      {
        type: "paragraph",
        text: "YouTube tutorials and free online resources are a starting point — but they almost never get you to job-ready. They're not sequenced, there's no accountability, no feedback, and they don't teach you how to actually work in a professional environment.",
      },
      {
        type: "innkeeper",
        title: "This is exactly why Innkeeper Talent Accelerator exists.",
        body: "Innkeeper is a Lagos-based tech training accelerator built specifically for Nigerians who want to break into tech — from complete beginners to those looking to level up. Every programme is instructor-led, industry-aligned, and structured to get you from zero to certified to employable.",
        checks: [
          "Industry-active mentors — not just instructors reading slides",
          "Peer-to-peer learning that keeps you accountable",
          "Global curriculum with local context",
          "Flexible payment options including instalment plans",
          "Courses designed around globally recognised certifications",
          "A community of learners and alumni in Lagos and beyond",
        ],
      },
      {
        type: "heading",
        text: "Step 4: Build While You Learn",
        step: 4,
      },
      {
        type: "paragraph",
        text: "Knowledge without application is useless in tech. From your very first week, start building things — even if they're small, even if they're imperfect.",
      },
      {
        type: "benefit-list",
        items: [
          {
            label: "Your portfolio is your CV.",
            detail:
              "Employers in tech want to see what you've built, not just what you've studied.",
          },
          {
            label: "Building solidifies learning.",
            detail:
              "You understand concepts far better when you apply them to real problems.",
          },
          {
            label: "Projects give you stories.",
            detail:
              "In interviews, 'I built X, it solved Y, I learned Z' is far more powerful than listing skills.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "At Innkeeper, building is baked into the programme. You don't finish the course and then start practising — you practise throughout, guided by mentors who've done it themselves.",
      },
      {
        type: "heading",
        text: "Step 5: Get Certified",
        step: 5,
      },
      {
        type: "paragraph",
        text: "Certifications are not just pieces of paper. In the Lagos tech market — and globally — they are proof. They tell employers that you have met a recognised standard, verified by an independent body.",
      },
      {
        type: "paragraph",
        text: "Certifications matter most in:",
      },
      {
        type: "benefit-list",
        items: [
          {
            label: "Networking.",
            detail: "The Cisco CCNA is a golden ticket into enterprise IT, telecoms, and banking.",
          },
          {
            label: "Cybersecurity.",
            detail: "Certifications like CompTIA Security+ and CEH are door-openers.",
          },
          {
            label: "Project Management.",
            detail: "The PMP and CAPM are globally respected and increasingly demanded.",
          },
          {
            label: "Cloud & AI.",
            detail: "AWS, Google, and Microsoft certifications are rising fast in Nigeria.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "Innkeeper programmes are specifically designed around certification prep. By the time you finish, you're not hoping you can pass the exam — you're ready.",
      },
      {
        type: "heading",
        text: "Step 6: Build Your Online Presence",
        step: 6,
      },
      {
        type: "paragraph",
        text: "Lagos tech companies Google you before they interview you. Your online presence is often the first impression you make — and it can either open doors or close them before you even know they existed.",
      },
      {
        type: "paragraph",
        text: "Three platforms matter most:",
      },
      {
        type: "numbered-list",
        items: [
          {
            icon: "💼",
            title: "LinkedIn",
            desc: "Update your profile to reflect your learning journey. Connect with people in your target industry. Recruiters in Nigeria's tech sector are actively searching LinkedIn every day.",
          },
          {
            icon: "⌨️",
            title: "GitHub",
            desc: "Every project you build should live here. A GitHub profile with regular commits signals to any technical recruiter that you are serious and consistent.",
          },
          {
            icon: "🐦",
            title: "Twitter / X",
            desc: "Lagos's tech community is active and vocal on Twitter. Engage with conversations, share your progress, follow founders and developers. Opportunities come through the timeline.",
          },
        ],
      },
      {
        type: "heading",
        text: "Step 7: Tap Into Lagos's Tech Community",
        step: 7,
      },
      {
        type: "paragraph",
        text: "Tech in Lagos is as much about who you know as what you know. The community here is genuinely supportive — and more connected than most people realise.",
      },
      {
        type: "benefit-list",
        items: [
          {
            label: "Tech events and meetups.",
            detail:
              "Events like Lagos Startup Week, Google Developer Groups, and local hackathons happen regularly. Show up.",
          },
          {
            label: "Co-working spaces.",
            detail:
              "Places like CcHub, Seedspace, and Workstation Lagos are ecosystems in themselves — great for networking and staying motivated.",
          },
          {
            label: "Online communities.",
            detail:
              "Slack groups, WhatsApp communities, and Discord servers for Nigerian developers are active and welcoming to beginners.",
          },
          {
            label: "Your training cohort.",
            detail:
              "The people you learn alongside at Innkeeper are future colleagues, collaborators, and referees. Treat those relationships seriously.",
          },
        ],
      },
      {
        type: "heading",
        text: "Step 8: Apply — Even Before You Feel Ready",
        step: 8,
      },
      {
        type: "paragraph",
        text: "Here's a truth that will save you months of waiting: you will never feel fully ready. The confidence comes after the application, not before it.",
      },
      {
        type: "paragraph",
        text: "Start applying for internships, junior roles, and freelance gigs as soon as you have a foundational skill and at least one or two portfolio projects. The worst that can happen is a 'no.' The best that can happen changes everything.",
      },
      {
        type: "paragraph",
        text: "In Lagos specifically, look at:",
      },
      {
        type: "benefit-list",
        items: [
          { label: "Jobberman & LinkedIn", detail: "For formal roles." },
          { label: "Twitter / X", detail: "For freelance opportunities posted by founders." },
          { label: "Upwork & Fiverr", detail: "For international freelance work." },
          { label: "Your network.", detail: "Referrals are the fastest route into most tech companies." },
        ],
      },
      {
        type: "cta",
        title: "Here's where Innkeeper comes in.",
        body: "Everything in this guide — the structure, the mentorship, the certifications, the community — is what Innkeeper Talent Accelerator is built to deliver. Spots are limited for the next cohort.",
        courses: [
          "💻 Frontend Web Development",
          "⚙️ Backend Web Development",
          "🔐 Cybersecurity",
          "🌐  Cisco Certified Network Associate (CCNA)",
          "🤖 AI & Machine Learning",
          "📋 Project Management",
        ],
        primaryLabel: "👉 Apply for Next Cohort",
        primaryHref: "https://innkeeper.work/apply-now",
        secondaryLabel: "View Curriculum",
        secondaryHref:
          "https://drive.google.com/file/d/1c1HXdgN62xQIeVsFVSpyX0zZmWA8doxC/view?usp=drive_link",
        note: "🎓 Flexible payment & instalment plans available · academy@innkeeper.work · +234 813 701 1735",
      },
    ],
  },
  {
    id: "top-10-ict-skills-lagos-2026",
    slug: "top-10-ict-skills-lagos-2026",
    title: "Top 10 ICT Skills to Learn in Lagos in 2026",
    excerpt: "Discover the most in-demand ICT skills in Lagos and how Magodo-based Innkeeper can help you turn them into a high-income career.",
    category: "ICT Skills",
    readTime: "10 min read",
    date: "2026-04-07",
    coverGradient: "linear-gradient(135deg, #002147 0%, #0f3d91 100%)",
    coverImage: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&q=80&auto=format&fit=crop",
    quote: {
      text: "The future is not one destination, but a series of skills you choose to learn.",
      author: "Innkeeper Insights",
    },
    content: [
      {
        type: "heading",
        text: "The Lagos Reality — And the Opportunity Most People Are Missing",
      },
      {
        type: "paragraph",
        text: "Walk through any major area in Lagos today — from Ikeja to Yaba — and you’ll see something striking. Young people are busy. Educated. Qualified on paper. Yet many are still searching, waiting, hoping. Not because they lack ambition, but because the rules of success have changed.",
      },
      {
        type: "paragraph",
        text: "We are no longer in an era where a degree guarantees a job, experience guarantees growth, or hard work alone guarantees income. Instead, we are in a skill-driven economy. And in 2026, one category of skills stands above the rest: <strong>ICT (Information and Communication Technology) skills</strong>",
      },
      {
        type: "paragraph",
        text: "These are the skills powering:",
      },
      {
        type: "bullets",
        items: [
          "Remote jobs that pay in dollars.",
          "Startups solving real problems.",
          "Businesses scaling faster than ever.",
        ],
      },
      {
        type: "paragraph",
        text: "So the real question is no longer:\n\n❌ “What did you study?”\n✅ “What can you do?”",
      },
      {
        type: "heading",
        text: "Why Learning ICT Skills in Lagos Is One of the Smartest Decisions You Can Make in 2026",
      },
      {
        type: "paragraph",
        text: "Lagos is not just Nigeria’s commercial capital — it is fast becoming West Africa’s digital engine. From fintech startups to global outsourcing opportunities, Lagos sits at the center of a transformation that quietly rewards those who are prepared.",
      },
      {
        type: "paragraph",
        text: "Here’s what makes it powerful:",
      },
      {
        type: "benefit-list",
        items: [
          {
            label: "Access to opportunity.",
            detail: "Tech companies, startups, and innovation hubs are concentrated here, making Lagos one of the best places in Africa to launch a digital career.",
          },
          {
            label: "Global reach.",
            detail: "With the right ICT skill, you can work for companies thousands of miles away and earn in dollars or pounds from Lagos.",
          },
          {
            label: "Income leverage.",
            detail: "Tech skills allow you to earn beyond local salary limits and build a career that scales faster than most traditional roles.",
          },
          {
            label: "Learning ecosystem.",
            detail: "From Yaba to Magodo, serious learners are positioning themselves early, and the support infrastructure is growing every day.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "And here’s the truth most people ignore: The opportunity is not scarce - preparation is.",
      },
      {
        type: "heading",
        text: "Top 10 ICT Skills to Learn in Lagos in 2026",
      },
      {
        type: "paragraph",
        text: "These are the skills powering remote jobs that pay in dollars, startups solving real problems, and businesses scaling faster than ever.",
      },
      {
        type: "heading",
        step: 1,
        text: "Data Analysis — The Skill That Turns Raw Numbers Into Business Power",
      },
      {
        type: "paragraph",
        text: "Every click, every transaction, every customer interaction generates information. But here’s the catch - data on its own is useless unless someone can interpret it. That someone is the <strong>Data Analyst</strong>.",
      },
      {
        type: "bullets",
        items: [
          "Data Analysis is one of the most accessible entry points into tech.",
          "You don’t need a computer science degree or advanced coding knowledge to start.",
          "Structured thinking and the willingness to learn tools matter more than pedigree.",
        ],
      },
      {
        type: "paragraph",
        text: "You’ll spend your day cleaning messy data, building dashboards that tell clear stories, and presenting insights that help businesses decide where to invest next.",
      },
      {
        type: "paragraph",
        text: "Tools that power this skill include Microsoft Excel, Power BI, and SQL.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦200K–₦800K/month, Remote $1,000–$5,000/month.",
      },
      {
        type: "reality",
        text: "Why this is a smart move: If you are looking for a skill that combines low entry barrier + strong income potential + wide job availability, Data Analysis is one of the most strategic choices you can make.",
      },
      {
        type: "heading",
        step: 2,
        text: "Cybersecurity — The Invisible Shield Protecting the Digital Economy",
      },
      {
        type: "paragraph",
        text: "As the world becomes more digital, it also becomes more vulnerable. Every system connected to the internet is a potential target, and when attacks happen the cost is massive.",
      },
      {
        type: "paragraph",
        text: "Cybersecurity professionals are not just needed — they are critical. Demand consistently exceeds supply, and companies are willing to pay premium salaries for the people who protect their assets.",
      },
      {
        type: "paragraph",
        text: "You’ll monitor systems for suspicious activity, identify vulnerabilities before attackers do, and strengthen digital defenses.",
      },
      {
        type: "paragraph",
        text: "Tools include Kali Linux, network monitoring tools, and ethical hacking frameworks.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦300K–₦1M/month, Remote $2,000+.",
      },
      {
        type: "reality",
        text: "Why this is a powerful path: If you want a career that combines prestige, purpose, and high income, Cybersecurity positions you at the center of digital protection.",
      },
      {
        type: "heading",
        step: 3,
        text: "Product (UI/UX) Design — Where Creativity Meets Technology",
      },
      {
        type: "paragraph",
        text: "Think about your favorite app. It feels easy to use, everything flows naturally, and you don’t have to think twice. That experience didn’t happen by accident - it was designed. Product (UI/UX) Designers are responsible for shaping how people interact with technology.",
      },
      {
        type: "paragraph",
        text: "Product Design does not require coding, rewards creativity and empathy, and has a relatively fast learning curve. It’s one of the fastest ways to enter tech from a non-technical background.",
      },
      {
        type: "paragraph",
        text: "You’ll design interfaces, map user journeys, and improve usability and engagement using tools like Figma and Adobe XD.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦200K–₦700K/month, Remote $1,500+.",
      },
      {
        type: "reality",
        text: "Why you should consider it: If you enjoy creative thinking and problem-solving, Product Design offers a perfect blend of both - with strong earning potential.",
      },
      {
        type: "heading",
        step: 4,
        text: "Frontend Development — Bringing Digital Experiences to Life",
      },
      {
        type: "paragraph",
        text: "Every website, web app, and digital interface you see had to be built by someone. Frontend developers translate design into  fully functional interactive experiences.",
      },
      {
        type: "paragraph",
        text: "Businesses continue to invest heavily in websites, web applications, and digital platforms, so the demand for frontend developers remains strong.",
      },
      {
        type: "paragraph",
        text: "You’ll turn designs into code, make websites responsive across devices, and improve user interactions with HTML, CSS, JavaScript, and React.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦250K–₦900K/month, Remote $2,000+.",
      },
      {
        type: "reality",
        text: "Why it’s worth it: If you like the idea of building things you can see and interact with, frontend development gives you that satisfaction - alongside strong income potential.",
      },
      {
        type: "heading",
        step: 5,
        text: "Backend Development — The Hidden Engine Behind Powerful Systems",
      },
      {
        type: "paragraph",
        text: "While frontend is what users see, backend is what makes everything function seamlessly. It handles data processing, server logic, and application performance.",
      },
      {
        type: "paragraph",
        text: "Backend developers build APIs, manage databases, and ensure systems run efficiently using tools like Node.js, Python, and modern databases.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦300K–₦1.2M/month, Remote $3,000+.",
      },
      {
        type: "reality",
        text: " Why this path is powerful: If you enjoy logic, structure, and problem-solving, backend development positions you for long-term growth.",
      },
      {
        type: "heading",
        step: 6,
        text: "Digital Marketing — Turning Attention Into Revenue",
      },
      {
        type: "paragraph",
        text: "Attention is currency in today’s digital world. Digital Marketing helps businesses get seen, attract customers, and generate revenue.",
      },
      {
        type: "paragraph",
        text: "This is one of the fastest skills you can learn and monetize. You can work for companies, freelance, or start your own business.",
      },
      {
        type: "paragraph",
        text: "You’ll run advertising campaigns, manage social media, and optimize search visibility with tools like Google Ads, Facebook Ads, and SEO tools.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦150K–₦600K/month, Freelance: Unlimited.",
      },
      {
        type: "reality",
        text: " Why it’s a strong choice: If you want a skill that quickly translates into income, Digital Marketing is one of the most practical options.",
      },
      {
        type: "heading",
        step: 7,
        text: "AI for Business Productivity — The Skill of the Future, Available Today",
      },
      {
        type: "paragraph",
        text: "Artificial Intelligence is no longer a futuristic concept — it is already reshaping how businesses operate. From automating tasks to improving decision-making, AI is becoming a core business advantage.",
      },
      {
        type: "paragraph",
        text: "Businesses want people who can use AI tools effectively, automate workflows, and improve productivity. That makes this one of the most in-demand skills today.",
      },
      {
        type: "paragraph",
        text: "You’ll implement tools like ChatGPT, automate processes, and help teams work smarter.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦300K+, Remote $2,000+.",
      },
      {
        type: "reality",
        text: " Why you should act early: This is still a relatively new field - which means early adopters gain the biggest advantage.",
      },
      {
        type: "heading",
        step: 8,
        text: "Product Management — The Strategic Mind Behind Successful Products",
      },
      {
        type: "paragraph",
        text: "Every successful digital product needs direction. Product Managers ensure the right features are built, teams stay aligned, and products succeed in the market.",
      },
      {
        type: "paragraph",
        text: "This role sits at the intersection of business, technology, and user experience — making it powerful for anyone who wants to move into leadership and strategy.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦400K–₦1.5M/month.",
      },
      {
        type: "reality",
        text: " Why it stands out: If you want to move beyond execution into strategy and leadership, Product Management is your path.",
      },
      {
        type: "heading",
        step: 9,
        text: "DevOps Engineering — The Backbone of Scalable Technology",
      },
      {
        type: "paragraph",
        text: "DevOps is where development meets operations. It focuses on making software delivery faster, more reliable, and more scalable.",
      },
      {
        type: "paragraph",
        text: "DevOps remains an elite skill because there are fewer engineers than demand, which drives salaries higher and gives you global relevance.",
      },
      {
        type: "paragraph",
        text: "You’ll work with tools like AWS, Docker, and Kubernetes to build reliable systems that can grow with any business.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦500K–₦2M/month, Remote $4,000+.",
      },
      {
        type: "reality",
        text: "  Why it’s worth pursuing: If your goal is top-tier income and global relevance, DevOps is one of the strongest positions you can aim for.",
      },
      {
        type: "heading",
        step: 10,
        text: "Project Management — Turning Plans Into Results",
      },
      {
        type: "paragraph",
        text: "Ideas are everywhere — execution is rare. Project Managers ensure that projects move from concept to completion efficiently.",
      },
      {
        type: "paragraph",
        text: "Every organisation needs structure, timelines, and coordination. If you’re naturally organised and enjoy working with people, this is a high-impact career path.",
      },
      {
        type: "paragraph",
        text: "Income Potential: Nigeria ₦250K–₦1M/month.",
      },
      {
        type: "reality",
        text: "Why it’s valuable: If you’re naturally organized and enjoy coordinating people and processes, this is a high-impact career path.",
      },
      {
        type: "heading",
        text: "Where to Learn ICT Skills in Lagos",
      },
      {
        type: "paragraph",
        text: "If you’re serious about learning these skills, the environment you choose matters more than you think. Innkeeper ICT Institute, located in Magodo, provides a structured pathway from beginner to professional.",
      },
      {
        type: "innkeeper",
        title: "Magodo's best environment for serious learners.",
        body: "At Innkeeper, you don’t just learn theory — you gain hands-on experience, real-world projects, internship opportunities, and job placement support.",
        checks: [
          "Structured training with local context.",
          "Hands-on projects from day one.",
          "Mentorship from industry professionals.",
          "Career support for Lagos and remote roles.",
        ],
      },
      {
        type: "heading",
        text: "The Difference Is Action",
      },
      {
        type: "paragraph",
        text: "Right now, there are people in Lagos who will read this and move on, and there are others who will take action and change their lives in months. The difference is not intelligence, the difference is decision.",
      },
      {
        type: "heading",
        text: "FAQs",
      },
      {
        type: "numbered-list",
        items: [
          {
            icon: "❓",
            title: "What is the best ICT skill to learn in 2026?",
            desc: "Data Analysis, Cybersecurity, and DevOps are among the top choices depending on your interest and income goals.",
          },
          {
            icon: "✅",
            title: "Can I start without experience?",
            desc: "Yes — many of these skills are beginner-friendly, and a structured programme can help you begin at any level.",
          },
          {
            icon: "⏱",
            title: "How long does it take?",
            desc: "Typically 3–6 months for foundational skills and real momentum if you stay consistent.",
          },
        ],
      },
      {
        type: "cta",
        title: "Ready to make 2026 your year?",
        body: "If you’re ready to move forward, Innkeeper ICT Institute in Magodo is a strong place to start. Choose a skill path, build experience, and claim a future that pays you better.",
        primaryLabel: "Apply Now",
        primaryHref: "https://innkeeper.work/apply-now",
        secondaryLabel: "View Learnings",
        secondaryHref: "https://drive.google.com/file/d/1c1HXdgN62xQIeVsFVSpyX0zZmWA8doxC/view?usp=drive_link",
        note: "Need help choosing the right path? academy@innkeeper.work • +234 813 701 1735",
      },
    ],
  },
  /* ─────────────────────────────────────────────────────────────
     POST 4: Weekend Tech Classes in Lagos
  ───────────────────────────────────────────────────────────── */
  {
    id: "weekend-tech-classes-lagos",
    slug: "weekend-tech-classes-lagos",
    title: "Weekend Tech Classes in Lagos for Working Professionals",
    excerpt:
      "You have a job. You have responsibilities. And somewhere in the middle of it all, you've been quietly wondering — is there a way to build tech skills without quitting everything? The answer is yes.",
    category: "Career Guide",
    readTime: "7 min read",
    date: "2026-04-22",
    coverGradient: "linear-gradient(135deg, #00114c 0%, #001a6e 60%, #00114c 100%)",
    coverImage: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=900&q=80&auto=format&fit=crop",
    quote: {
      text: "You have a job. You have responsibilities. And somewhere in the middle of all of it, you've been quietly wondering — is there a way to build tech skills without quitting everything you've already built?",
      author: "Innkeeper Talent Accelerator",
    },
    content: [
      // ── Opening ──────────────────────────────────────────────
      {
        type: "paragraph",
        text: "The answer is yes. And Lagos, arguably the most energetic tech ecosystem on the continent, now has more structured weekend learning options than ever before. This guide breaks down everything you need to know — what's available, what to look for, and how to choose the right programme without wasting your time or money.",
      },

      // ── Section 1 ──────────────────────────────────────────────
      {
        type: "heading",
        text: "Why Weekend Learning Makes Sense for Professionals",
      },
      {
        type: "paragraph",
        text: "The traditional model of full-time bootcamps or weekday evening classes simply doesn't work for most working professionals in Lagos. Between commutes, work demands, and family commitments, Monday to Friday schedules are already fully accounted for. Weekend cohorts typically Saturday and sometimes Sunday sessions are structured to solve exactly this problem.",
      },
      {
        type: "benefit-list",
        items: [
          {
            label: "Dedicated learning blocks —",
            detail: "3–5 hours of focused, structured learning per session without the distraction of your workday.",
          },
          {
            label: "Weekly momentum —",
            detail: "consistent enough to build real skill progression, without overwhelming your schedule.",
          },
          {
            label: "Community —",
            detail: "you learn alongside other professionals who understand your constraints, creating a different kind of accountability than a classroom full of students.",
          },
          {
            label: "No career break required —",
            detail: "you continue earning while you learn, which makes the investment considerably lower risk.",
          },
        ],
      },
      {
        type: "tip",
        text: "3–5 hours every weekend across 8–14 weeks adds up to 80–140 hours of structured, hands-on learning. That's comparable to several university semester courses — compressed, industry-relevant, and immediately applicable.",
      },

      // ── Section 2 ──────────────────────────────────────────────
      {
        type: "heading",
        text: "What to Look for in a Weekend Tech Programme",
      },
      {
        type: "paragraph",
        text: "Not all weekend classes are created equal. Before you enrol anywhere, here are the five questions worth asking to protect your time and money.",
      },
      {
        type: "numbered-list",
        items: [
          {
            icon: "1",
            title: "Is it instructor-led or self-paced?",
            desc: "Self-paced content gives you flexibility but almost zero accountability. Most working professionals who try to learn through pre-recorded videos alone don't finish. Instructor-led weekend cohorts — where you show up at a set time and work through problems in real time — have significantly higher completion rates.",
          },
          {
            icon: "2",
            title: "Who is teaching?",
            desc: "The gap between an instructor who teaches for a living and one who is actively working in the industry is enormous. Ask whether your facilitator is currently employed in the field they're teaching. Real practitioners bring current tools, workflows, and real hiring insight to the classroom.",
          },
          {
            icon: "3",
            title: "What is the measurable outcome?",
            desc: "A good weekend programme should be clear about what you will be able to do by the end — not just what topics you'll cover. Look for programmes that specify certifications, portfolio projects, or career paths as measurable outcomes.",
          },
          {
            icon: "4",
            title: "Is there flexibility in payment?",
            desc: "A ₦300,000–₦600,000 course fee is a real commitment on a professional salary. Look for providers that offer instalment plans or Income Share Agreements (ISAs), where you pay nothing upfront and repay only once you've landed a higher-paying role.",
          },
          {
            icon: "5",
            title: "Is the provider accredited?",
            desc: "Accreditation matters more than most people realise. It's the difference between a certificate employers recognise and one that collects dust. Look for NBTE accreditation, affiliation with globally recognised bodies like Cisco Networking Academy or VMware IT Academy, or curriculum aligned to internationally recognised certification exams.",
          },
        ],
      },

      // ── Section 3 ──────────────────────────────────────────────
      {
        type: "heading",
        text: "The Best Tech Skills to Learn on Weekends in 2026",
      },
      {
        type: "paragraph",
        text: "Some tech tracks are better suited to weekend learning than others. Below are the disciplines that deliver strong career outcomes for professionals who study part-time.",
      },
      {
        type: "card-grid",
        items: [
          {
            icon: "🤖",
            title: "AI & Automation",
            desc: "AI Essentials and Automation courses run 4–8 weeks — the fastest path to immediate workplace impact for non-technical professionals.",
          },
          {
            icon: "☁️",
            title: "Cloud Engineering",
            desc: "Nigeria's cloud market is projected to reach $3.28B by 2030. AWS, GCP, and Azure certifications are in high demand in banking and fintech.",
          },
          {
            icon: "🔒",
            title: "Cybersecurity",
            desc: "Banks, telecoms, and government agencies are actively hiring SOC Analysts. One of the most weekend-friendly disciplines.",
          },
          {
            icon: "📊",
            title: "Data Engineering",
            desc: "SQL, Python, dbt, and cloud data warehouses can transform a business analyst into an engineer earning ₦280k–₦500k/month remotely.",
          },
          {
            icon: "🧩",
            title: "No-Code & Vibe Coding",
            desc: "A 6-week weekend course is enough for entrepreneurs and PMs to ship a working web app, dashboard, or internal tool.",
          },
          {
            icon: "📝",
            title: "Technical Writing",
            desc: "Earn $2,000–$6,000/month on remote contracts. Learnable in 8 weeks, with almost no competition in Nigeria.",
          },
        ],
      },

      // ── Section 4 ──────────────────────────────────────────────
      {
        type: "heading",
        text: "Weekend Tech Learning in Lagos: Physical vs. Virtual",
      },
      {
        type: "paragraph",
        text: "Lagos traffic is a real variable in any decision about weekend learning. A Saturday class in Lekki that requires a 2-hour commute from Ikorodu is not the same as a class that starts at 9:00 AM on your laptop.",
      },
      {
        type: "bullets",
        items: [
          "Virtual-only: Maximum flexibility, zero commute. Best for self-motivated learners or those outside Lagos Island. Risk: lower social accountability and networking.",
          "Blended (virtual + physical): The best of both worlds. Virtual sessions for content delivery; physical sessions for labs, networking, and human connection. Increasingly the standard for serious professional programmes.",
          "Physical-only: Highest accountability and networking, but requires proximity to the campus. Best suited to learners who need structure to stay consistent.",
        ],
      },
      {
        type: "tip",
        text: "Innkeeper Talent Accelerator offers blended and fully virtual weekend cohorts across Lagos campuses in Magodo, Ikorodu, Agege, and Yaba — as well as a physical campus in Akure. All programmes are available virtually with the same instructor-led quality as in-person sessions.",
      },

      // ── Section 5 ──────────────────────────────────────────────
      {
        type: "heading",
        text: "How to Fit Weekend Learning Into a Busy Professional Life",
      },
      {
        type: "paragraph",
        text: "The biggest obstacle to weekend learning isn't time — it's energy management. The professionals who complete these programmes are not the ones with the most free time. They are the ones who made a deliberate decision and protected it.",
      },
      {
        type: "bullets",
        items: [
          "Protect Saturday mornings. Treat your weekend class like a non-negotiable client meeting. It goes in the calendar first, everything else works around it.",
          "Do your pre-work on Friday evenings. Even 30–45 minutes of reviewing notes before the weekend session dramatically improves how much you absorb.",
          "Use your commute. If your programme offers recordings or reading materials, Monday-to-Friday commutes are prime learning time.",
          "Find a study partner. Someone in your cohort who keeps you honest. Accountability doubles completion rates.",
          "Communicate with your household. If your family understands that Saturday 9–12 AM is non-negotiable for 10 weeks, the friction disappears.",
        ],
      },
      {
        type: "reality",
        text: "The professionals who complete weekend tech programmes are not the ones with the most free time. They are the ones who made a deliberate decision and protected it. The time exists — the question is always whether the goal is important enough to protect it.",
      },

      // ── Innkeeper block ─────────────────────────────────────────
      {
        type: "innkeeper",
        title: "Why Innkeeper is the Right Choice for Weekend Tech Learning",
        body: "There are a number of providers offering weekend tech classes in Lagos. Innkeeper Talent Accelerator stands apart for a specific set of reasons that matter particularly to working professionals: accreditation, industry-active facilitators, flexible payment options, and a proven track record with 1,000+ graduates.",
        checks: [
          "NBTE Accredited — Reg. No: STC [1301751]",
          "Cisco Networking Academy Affiliate — ID: 400056597",
          "VMware IT Academy Partner",
          "Industry-active facilitators (not career academics)",
          "Income Share Agreement — pay nothing upfront",
          "80% placement rate within 90 days of graduation",
          "1,000+ talents trained across Nigeria",
          "Campuses: Magodo, Ikorodu, Agege, Yaba + Akure",
        ],
      },

      // ── Programme table ─────────────────────────────────────────
      {
        type: "heading",
        text: "2026 Programme Overview",
      },
      {
        type: "paragraph",
        text: "All programmes are available with installment payment plans. ISA (Income Share Agreement) is available on selected courses.",
      },
      {
        type: "programme-table",
        rows: [
          { programme: "AI Essentials for Business",      duration: "4 Wks",  fee: "100,000", mode: "Online - Self-paced",   modeColor: "#29aae2", bestFor: "Executives & managers" },
          { programme: "AI Automation",                   duration: "8 Wks",  fee: "150,000", mode: "Online - Weekend",   modeColor: "#29aae2", bestFor: "Ops & business teams" },
          { programme: "Vibe Coding – No-Code Builder",   duration: "6 Wks",  fee: "150,000", mode: "Online - Weekend",   modeColor: "#29aae2", bestFor: "Entrepreneurs & PMs" },
          { programme: "Technical Writing & DevRel",      duration: "8 Wks",  fee: "300,000", mode: "Online - Weekend",   modeColor: "#29aae2", bestFor: "Writers & communicators" },
          { programme: "Cybersecurity – SOC Analyst",     duration: "14 Wks", fee: "400,000", mode: "Blended - Weekend",  modeColor: "#00114c", bestFor: "IT professionals pivoting" },
          { programme: "Data Engineering & Analytics",    duration: "14 Wks", fee: "500,000", mode: "Blended",  modeColor: "#00114c", bestFor: "Analysts & SQL users" },
          { programme: "Cloud Engineering (AWS/GCP/Azure)",duration: "16 Wks",fee: "600,000", mode: "Blended",  modeColor: "#00114c", bestFor: "Network & IT admins" },
          { programme: "DevOps & DevSecOps",              duration: "14 Wks", fee: "500,000", mode: "Blended + Labs",  modeColor: "#00114c", bestFor: "Backend devs & sysadmins" },
          { programme: "AI & ML Engineering",             duration: "18 Wks", fee: "600,000", mode: "Instructor-led + Lab",  modeColor: "#00114c", bestFor: "Data scientists & devs" },
          { programme: "Fintech & ERP Systems",           duration: "12 Wks", fee: "350,000", mode: "Blended + Sim Lab",  modeColor: "#00114c", bestFor: "Finance & business analysts" },
          { programme: "Software QA & Automation",        duration: "10 Wks", fee: "400,000", mode: "Blended",  modeColor: "#00114c", bestFor: "Devs & business analysts" },
          { programme: "Embedded Systems & IoT",          duration: "14 Wks", fee: "300,000", mode: "Blended + Hardware",  modeColor: "#00114c", bestFor: "Electrical engineers" },
        ],
      },

      // ── CTA ────────────────────────────────────────────────────
      {
        type: "cta",
        title: "The Weekend Is Enough.",
        body: "Every professional in Lagos who has thought about building a tech skill has told themselves they'll start when things slow down. Things don't slow down. But weekends keep coming — and every one of them is an opportunity.",
        courses: [
          "AI & Automation",
          "Cloud Engineering",
          "Cybersecurity",
          "Data Engineering",
          "Technical Writing",
          "Vibe Coding",
        ],
        primaryLabel: "Enrol for 2026",
        primaryHref: "https://innkeeper.work/apply-now",
        secondaryLabel: "View All Programmes",
        secondaryHref: "https://drive.google.com/file/d/1c1HXdgN62xQIeVsFVSpyX0zZmWA8doxC/view?usp=drive_link",
        note: "ISA available on selected courses · Instalment plans on all programmes · academy@innkeeper.work · +234 813 701 1735",
      },
    ],
  },
  /* ─────────────────────────────────────────────────────────────
   POST 5: How to Build a Live Portfolio Website Using Claude
───────────────────────────────────────────────────────────── */
  {
    id: "build-portfolio-website-claude-free",
    slug: "build-portfolio-website-claude-free",
    title: "How to Build a Live Portfolio Website Using Claude's Free Plan — No Coding Required",
    excerpt:
      "Your portfolio speaks louder than your CV. Here's how to build and publish a real, live portfolio website for free, without writing a single line of code from scratch.",
    category: "Step-by-Step",
    readTime: "8 min read",
    date: "2026-04-23",
    coverGradient: "linear-gradient(135deg, #0a0a2e 0%, #00114c 50%, #001a6e 100%)",
    coverImage: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=900&q=80&auto=format&fit=crop",
    quote: {
      text: "You need a free Claude account, your story, and about an hour. That's it.",
      author: "Innkeeper Insights",
    },
    content: [

      // ── Opening ──────────────────────────────────────────────
      {
        type: "paragraph",
        text: "One of the most common pieces of advice in the tech world is this: your portfolio speaks louder than your CV. But here's the problem most beginners run into building a portfolio website sounds like something you can only do after you've already learned to code. It feels like a chicken-and-egg situation. You need the portfolio to get the job, but you need the skills to build the portfolio.",
      },
      {
        type: "paragraph",
        text: "What if you could sidestep that entirely? In this guide, we'll walk you through how to build and publish a real, live portfolio website using Claude's free plan without writing a single line of code from scratch. By the end, you'll have a working site on the internet that you can share with employers, clients, and your network.",
      },
      {
        type: "tip",
        text: "Who this is for: Anyone learning tech skills i.e frontend development, cybersecurity, data, AI, QA, or any other field who wants a professional online presence before they feel 'ready.' You do not need any prior coding knowledge to follow this guide.",
      },

      // ── What Claude's Free Plan Can Do ───────────────────────
      {
        type: "heading",
        text: "What Can Claude's Free Plan Actually Do?",
      },
      {
        type: "paragraph",
        text: "Before we get into the steps, it's worth being clear about what you're working with so there are no surprises. Claude's free plan gives you access to the Claude Sonnet model, the same model used by millions of professionals worldwide. As of 2026, the free plan also includes access to Artifacts and Projects, which are exactly the features you need for this tutorial.",
      },
      {
        type: "benefit-list",
        items: [
          {
            label: "Access to Claude Sonnet:",
            detail: "Powerful enough for coding, writing, and building full web pages.",
          },
          {
            label: "Artifacts:",
            detail: "Claude can create interactive HTML files you can preview and download instantly.",
          },
          {
            label: "Projects:",
            detail: "Organise your conversation and uploads so Claude remembers your context across messages.",
          },
          {
            label: "Usage limits apply:",
            detail: "Free users get approximately 15–40 messages per 5-hour window. For this project, spread your work across sessions if needed.",
          },
        ],
      },
      {
        type: "reality",
        text: "If you hit the free usage limit mid-session, simply wait 5 hours for it to reset or continue the next day. Because we're saving your work as downloadable HTML files at each step, you won't lose any progress.",
      },

      // ── What You'll Need ─────────────────────────────────────
      {
        type: "heading",
        text: "What You'll Need Before You Start",
      },
      {
        type: "paragraph",
        text: "Gather these before your first Claude session, having them ready means you can move fast and get everything built in one sitting:",
      },
      {
        type: "bullets",
        items: [
          "A free Claude account — sign up at claude.ai. No credit card required.",
          "Your CV or a short bio — name, skills, what you're studying or working on, and any projects you've done. Even rough notes work.",
          "2–3 project descriptions — these don't have to be finished projects. A course project, a personal project, or something you built while learning all count.",
          "A free Netlify account — you'll use this to publish the site live. Sign up at netlify.com. Completely free for personal projects.",
          "A browser — that's it. No code editor, no terminal, no installations.",
        ],
      },

      // ── Step 1 ────────────────────────────────────────────────
      {
        type: "heading",
        step: 1,
        text: "Tell Claude to Build Your Portfolio",
      },
      {
        type: "paragraph",
        text: "Go to claude.ai and start a new conversation. You're going to give Claude a single, detailed prompt that describes exactly what you want. The more context you give, the better the result.",
      },
      {
        type: "innkeeper",
        title: "Paste this prompt into Claude: Customise the details in brackets:",
        body: "Build me a professional portfolio website as a single HTML file. Here are my details:\n\nName: [Your Name]\nRole/Title: [e.g. Aspiring Frontend Developer | Cybersecurity Student | Data Analyst]\nAbout me: [2–3 sentences about who you are and what you're building towards]\nSkills: [List your skills e.g. HTML, CSS, JavaScript, Python, networking, etc.]\n\nProjects:\n1. [Project name] — [Brief description of what it does / what you built]\n2. [Project name] — [Brief description]\n3. [Project name] — [Brief description]\n\nDesign preferences: Make it look modern and professional. Dark navy and cyan colour scheme. Clean layout. Mobile-friendly. Include sections for About, Skills, Projects, and Contact. Output the complete, working HTML file as an Artifact I can download.",
        checks: [
          "Is your name and title displayed correctly?",
          "Are all your projects listed?",
          "Does the colour scheme look clean?",
          "Is there any placeholder text still showing?",
        ],
      },
      {
        type: "paragraph",
        text: "Claude will generate a full HTML portfolio page as an Artifact, with a live preview panel on the right side of the screen. You'll be able to see exactly what it looks like before you download anything. Note anything you want changed you'll fix it in the next step.",
      },

      // ── Step 2 ────────────────────────────────────────────────
      {
        type: "heading",
        step: 2,
        text: "Refine It Until It Looks Right",
      },
      {
        type: "paragraph",
        text: "The first version is rarely perfect and that's completely normal. Claude is designed for iteration. Simply continue the conversation and tell it what you want changed. There's no limit to how many changes you can request in a single session just keep going until you're happy.",
      },
      {
        type: "numbered-list",
        items: [
          {
            icon: "🎨",
            title: "Change the design",
            desc: "\"I like the layout but the font feels too small. Make the headings bigger, add a bit more spacing between sections, and make the project cards have a subtle border.\"",
          },
          {
            icon: "✏️",
            title: "Add or update content",
            desc: "\"Update the About section to say: [your updated bio]. Also add a fourth project: [project name and description].\"",
          },
          {
            icon: "🔧",
            title: "Fix something specific",
            desc: "\"The contact section shows a placeholder email. Replace it with [your email]. Also add a link to my LinkedIn: [your LinkedIn URL].\"",
          },
          {
            icon: "✨",
            title: "Make it more impressive",
            desc: "\"Add a subtle fade-in animation when the page loads. Make the project cards lift slightly when hovered over. Keep everything clean and professional.\"",
          },
        ],
      },

      // ── Step 3 ────────────────────────────────────────────────
      {
        type: "heading",
        step: 3,
        text: "Download Your HTML File",
      },
      {
        type: "paragraph",
        text: "Once you're happy with how your portfolio looks, it's time to download the file.",
      },
      {
        type: "bullets",
        items: [
          "In the Artifact panel (the preview on the right), look for the download or copy icon at the top right of the panel.",
          "Click download this saves your portfolio as a file called something like index.html to your computer.",
          "Open the file in your browser and double-click the downloaded file. It will open in Chrome, Firefox, or Safari and look exactly like the preview.",
          "Do a final check and scroll through the whole page. Make sure everything looks right, all links work, and there's no placeholder text remaining.",
        ],
      },
      {
        type: "reality",
        text: "Name your file correctly: rename your downloaded file to exactly index.html (all lowercase). This is important for Netlify to recognise it as your homepage when you publish.",
      },

      // ── Step 4 ────────────────────────────────────────────────
      {
        type: "heading",
        step: 4,
        text: "Publish It Live on Netlify",
      },
      {
        type: "paragraph",
        text: "This is the part that turns your local HTML file into a real website on the internet with a shareable link you can put on your CV, LinkedIn, and emails.",
      },
      {
        type: "benefit-list",
        items: [
          {
            label: "Sign up at Netlify —",
            detail: "go to netlify.com and click Sign Up. Use your GitHub, GitLab, or email address any option works.",
          },
          {
            label: "Go to your dashboard —",
            detail: "once logged in, find the 'Sites' section or 'Add new site' button.",
          },
          {
            label: "Drag and drop your file —",
            detail: "look for the zone that says 'Deploy manually' and drag your index.html file directly into it.",
          },
          {
            label: "Your site goes live —",
            detail: "wait 10–30 seconds. Netlify will generate a URL like: random-name-123.netlify.app",
          },
          {
            label: "Optional: get a custom URL —",
            detail: "go to Site Settings → Domain Management to rename it to yourname.netlify.app for free.",
          },
        ],
      },
      {
        type: "tip",
        text: "Congratulations your portfolio is now live on the internet, accessible from any device, anywhere in the world. Copy that Netlify URL and add it to your CV, LinkedIn profile, and email signature immediately.",
      },

      // ── Step 5 ────────────────────────────────────────────────
      {
        type: "heading",
        step: 5,
        text: "Update It As You Grow",
      },
      {
        type: "paragraph",
        text: "Your portfolio isn't a one-time project. It's a living document that should grow with your skills. Every new project you complete, every certification you earn, every skill you add is worth updating.",
      },
      {
        type: "bullets",
        items: [
          "Go back to Claude and start a new conversation (or continue an existing Project if you saved your work there).",
          "Paste in your current HTML code and ask Claude to make the specific changes you want.",
          "Download the updated HTML file.",
          "Drag the new file into Netlify, it will automatically replace the old version. Your URL stays the same.",
        ],
      },
      {
        type: "reality",
        text: "Create a Project in Claude and paste your final HTML code there as a note. This means every time you come back to update your portfolio, you can paste it back in and continue where you left off without starting from scratch.",
      },

      // ── What to Add Next ─────────────────────────────────────
      {
        type: "heading",
        text: "What to Add to Your Portfolio Next",
      },
      {
        type: "paragraph",
        text: "Once your basic portfolio is live, here are additions worth asking Claude to help with in future sessions:",
      },
      {
        type: "card-grid",
        items: [
          {
            icon: "📬",
            title: "Contact Form",
            desc: "Ask Claude to add a working contact form using a free service like Formspree, no backend required.",
          },
          {
            icon: "🖼️",
            title: "Project Screenshots",
            desc: "Upload images to Claude and ask it to embed them in your project cards for a more visual, polished look.",
          },
          {
            icon: "📝",
            title: "Blog Section",
            desc: "A blog or articles section is great for SEO and signals to employers that you're actively engaged in your field.",
          },
          {
            icon: "📄",
            title: "Resume Download Button",
            desc: "Link to a PDF version of your CV so employers can download it directly from your portfolio.",
          },
          {
            icon: "🌗",
            title: "Dark / Light Mode Toggle",
            desc: "A small but impressive interactive feature that shows technical attention to detail.",
          },
          {
            icon: "🏅",
            title: "Certificate Badges",
            desc: "Add images of your Innkeeper, Cisco, or CompTIA certificates as you earn them. Social proof that builds trust instantly.",
          },
        ],
      },

      // ── Free Plan Tips ────────────────────────────────────────
      {
        type: "heading",
        text: "Working Within the Free Plan — Practical Tips",
      },
      {
        type: "paragraph",
        text: "If you're using the free plan consistently, here are a few habits that make everything smoother:",
      },
      {
        type: "bullets",
        items: [
          "Work in sessions. Break the project into: (1) build the structure, (2) refine the design, (3) add animations. Don't try to build everything in one conversation.",
          "Save your HTML after every session. Always download the latest version of your file before closing Claude. It takes 5 seconds and means you never lose work.",
          "Be specific in your prompts. 'Make it look better' gives Claude too little to work with. 'Make the project cards have a dark navy background, cyan border, and 16px padding' gives exactly what it needs.",
          "Use Projects to save context. Create a Claude Project for your portfolio and paste your current HTML code as a saved note so Claude always has full context.",
          "If you hit the limit, wait and return. The reset window is 5 hours. Your saved HTML file means zero progress lost.",
        ],
      },

      // ── CTA ────────────────────────────────────────────────────
      {
        type: "cta",
        title: "A live portfolio changes how people see you.",
        body: "You don't need to know how to code to have a portfolio. You don't need a paid subscription. You don't need expensive tools. You need a free Claude account, your story, and about an hour. Start today.",
        courses: [
          "🖥️ Frontend Development",
          "🔒 Cybersecurity",
          "📊 Data Engineering",
          "☁️ Cloud Engineering",
          "📝 Technical Writing",
          "🤖 AI & Automation",
        ],
        primaryLabel: "Start Learning at Innkeeper",
        primaryHref: "https://innkeeper.work/apply-now",
        secondaryLabel: "View All Programmes",
        secondaryHref: "https://drive.google.com/file/d/1c1HXdgN62xQIeVsFVSpyX0zZmWA8doxC/view?usp=drive_link",
        note: "Questions? academy@innkeeper.work · +234 813 701 1735 · Flexible payment & ISA available",
      },
    ],
  },
];

/** Helper: get a single post by slug */
export const getPostBySlug = (slug) =>
  BLOGS.find((b) => b.slug === slug) || null;
